/**
 * The columns of an open-claims list: the Terminated worklists page's panel and the
 * person page's pile (review #158) show the same claims the same way.
 *
 * By visit ID, not claim ID (review #720, Shayna 2026-10-18: "remove claim ID. Instead
 * give me visit ID."). The claim id stays the rows' key.
 */
import type { Column } from '@/components/Table'
import type { ExportColumn } from '@/ui/export'
import { AGE_LABEL, AGE_ORDER, type OpenClaim } from '@/data/terminatedClaims'
import { dateLong, money2 } from '@/format'
import { DIMENSION_HELP } from '@/measures/definitions'

/**
 * `worklist` false leaves the column off: one worklist's claims all sit on it.
 * `onVisit` makes each Visit ID open that visit on Claim detail (useOpenClaimVisit).
 */
export function openClaimColumns(worklist: boolean, onVisit?: (visit: string) => void): Column<OpenClaim>[] {
  return [
    {
      key: 'visit',
      header: 'Visit ID',
      width: 96,
      sort: (c) => c.visitId,
      render: (c) =>
        c.visitId && onVisit ? (
          <button
            type="button"
            className="cell-link mono"
            data-testid="open-claim-visit"
            title={`Visit ${c.visitId} on Claim detail, if it was billed`}
            onClick={() => onVisit(c.visitId!)}
          >
            {c.visitId}
          </button>
        ) : (
          <span className="mono">{c.visitId ?? '—'}</span>
        ),
    },
    ...(worklist
      ? [
          {
            key: 'worklist',
            header: 'Worklist',
            width: 220,
            sort: (c: OpenClaim) => c.worklist,
            render: (c: OpenClaim) => <span className="tw-name">{c.worklist}</span>,
          },
        ]
      : []),
    {
      key: 'facility',
      header: 'Facility',
      width: 180,
      sort: (c) => c.facility,
      render: (c) => c.facility,
    },
    {
      key: 'payerType',
      header: 'Payer type',
      help: DIMENSION_HELP.payerType,
      width: 96,
      sort: (c) => c.payerType,
      render: (c) => c.payerType,
    },
    {
      key: 'payer',
      header: 'Payer name',
      help: DIMENSION_HELP.payerName,
      width: 170,
      sort: (c) => c.payer || null,
      render: (c) => c.payer || '—',
    },
    {
      key: 'dos',
      header: 'Date of service',
      width: 110,
      sort: (c) => c.dos,
      render: (c) => <span className="mono">{dateLong(c.dos)}</span>,
    },
    {
      key: 'bill',
      header: 'Bill date',
      width: 110,
      sort: (c) => c.billDate,
      render: (c) => <span className="mono">{dateLong(c.billDate)}</span>,
    },
    {
      key: 'touch',
      header: 'Last touched',
      width: 110,
      title: 'Last touch by anyone, on or before the snapshot date',
      sort: (c) => c.lastTouch,
      render: (c) => <span className="mono">{dateLong(c.lastTouch)}</span>,
    },
    {
      key: 'age',
      header: 'Last worked',
      width: 120,
      sort: (c) => AGE_ORDER[c.age] ?? null,
      render: (c) => AGE_LABEL[c.age] ?? c.age,
    },
    {
      key: 'rem',
      header: 'Balance',
      align: 'right',
      width: 100,
      sort: (c) => c.remaining,
      render: (c) => <span className="mono">{money2(c.remaining)}</span>,
    },
  ]
}

/** The claim's own columns in an export; the page puts whose they are in front. */
export const OPEN_CLAIM_EXPORT: ExportColumn<OpenClaim>[] = [
  { header: 'Visit ID', value: (c) => c.visitId ?? '' },
  { header: 'Worklist', value: (c) => c.worklist },
  { header: 'Facility', value: (c) => c.facility },
  { header: 'Payer type', value: (c) => c.payerType },
  { header: 'Payer name', value: (c) => c.payer },
  { header: 'Claim type', value: (c) => c.claimType },
  { header: 'Date of service', value: (c) => c.dos ?? '' },
  { header: 'Bill date', value: (c) => c.billDate ?? '' },
  { header: 'Last touched', value: (c) => c.lastTouch ?? '' },
  { header: 'Last worked', value: (c) => AGE_LABEL[c.age] ?? c.age },
  { header: 'Balance', value: (c) => c.remaining ?? '' },
]
